import { curry, reduce, prop, compose, length } from 'ramda';

import CHILD_ROUTE from '../../config/routes/child-route';
import { asyncComponent } from '../../routing/AsyncComponent';
import { isVoid } from '../compact';
import { FR, EN } from '../i18n';

/**
 * Add cold page routes to route list
 * @param {Array} coldPages contains list of static articles
 * @param {Array} routes contains list of routes already created
 * @returns {Array} list of routes
 */
const addColdPageRoutes = curry((coldPages, routes) => [
  ...routes,
  ...reduce(
    (acc, { id, fr, en }) => [
      ...acc,
      {
        id,
        fr: `:articleUid(${fr})`,
        en: `:articleUid(${en})`,
        fileLoader: /* istanbul ignore next */ () =>
          import('../../containers/Article'),
        children: [CHILD_ROUTE.MEDIA],
      },
    ],
    [],
    coldPages
  ),
]);

/**
 * Add children routes to route list
 * @param {Array} routes
 * @param {Array} parentRoute (optional)
 * @returns {Array}
 */
const addChildrenRoutes = (routes, parentRoute) =>
  reduce(
    (acc, { children = [], ...route }) => {
      const r = parentRoute
        ? {
            ...route,
            id: `${parentRoute.id}__${route.id}`,
            fr: `${parentRoute.fr}/${route.fr}`,
            en: `${parentRoute.en}/${route.en}`,
            exact: true,
            // isPopOverRoute: true,
          }
        : { ...route, exact: !length(children) };
      return [...acc, ...addChildrenRoutes(children, r), r];
    },
    [],
    routes
  );

/**
 * Add a single route
 * @param {String} lng
 * @param {Object} route
 * @param {Object} configuration of all routes
 * @returns {Object} the new configuration
 */
const addRoute = (lng, route, { PATH_TO_ROUTE, ROUTES, ROUTE_TO_PATH }) => {
  let path = `/:lng(${lng})`;
  if (!isVoid(prop(lng, route))) {
    path = `${path}/${prop(lng, route)}`;
  }

  return {
    PATH_TO_ROUTE: {
      ...PATH_TO_ROUTE,
      [path]: route.id,
    },
    ROUTES: [
      ...ROUTES,
      {
        id: route.id,
        path,
        exact: route.exact,
        // isPopOverRoute: route.isPopOverRoute,
        component: asyncComponent({
          loader: route.fileLoader,
        }),
      },
      {
        path: `/${prop(lng, route)}`,
        exact: true,
        redirectTo: `/${lng}/${prop(lng, route)}`,
      },
    ],
    ROUTE_TO_PATH: {
      ...ROUTE_TO_PATH,
      [route.id]: {
        ...ROUTE_TO_PATH[route.id],
        [lng]: path,
      },
    },
  };
};

/**
 * Function to get a not-found route definition with the given props
 * @param {Object} configuration of all routes
 * @return {Object} the new configuration with error route
 */
const addNotFoundErrorRoute = ({ ROUTES, ...config }) => ({
  ...config,
  ROUTES: [
    ...ROUTES,
    {
      id: 'error',
      path: '',
      component: asyncComponent({
        loader: () => import('../../pages/ErrorPage'),
      }),
      httpStatusCode: 404,
    },
  ],
});

const makeRouteConfig = reduce(
  ({ ROUTE_KEY, ...acc }, route) => {
    let res = acc;
    if (route.fr !== undefined) {
      res = addRoute(FR, route, res);
    }
    if (route.en !== undefined) {
      res = addRoute(EN, route, res);
    }
    return {
      ...res,
      ROUTE_KEY: { ...ROUTE_KEY, [route.id]: route.id },
    };
  },
  { ROUTE_KEY: {}, PATH_TO_ROUTE: {}, ROUTE_TO_PATH: {}, ROUTES: [] }
);

const setup = (coldPages, routes) =>
  compose(
    addNotFoundErrorRoute,
    makeRouteConfig,
    addChildrenRoutes,
    addColdPageRoutes(coldPages)
  )(routes);

export default setup;
